import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { DataViewContext, DataViewDefinition } from '@adornis/data-view/client/data-view.js';
import { tableView } from '@adornis/data-view/client/table/table-view.js';
import type { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing, type TemplateResult } from 'lit';

/** Counts the records matching the given filter, server-side, with the same scoping as the list. */
export type RecordCounter = (filter: AdornisFilter) => Promise<number>;

const RecordCount = stateful(({ filter, count }: { filter: AdornisFilter; count: RecordCounter }) => {
  // The data-view wraps each list view in a lit `guard`, so this render is NOT re-invoked when the
  // filter changes. Subscribe to the (stable) filter's change stream ourselves and force a re-render
  // on every change, so the count tracks search / quick-filter edits — the same live behaviour the
  // DataTable has. We call `.subscribe` on the observable directly to avoid an rxjs import (this
  // package doesn't depend on rxjs).
  const [, forceUpdate] = useState<object>({});
  useEffect(() => {
    const changes = (filter as { whenChanging?: { subscribe?: (fn: () => void) => { unsubscribe?: () => void } } })
      .whenChanging;
    const subscription = changes?.subscribe?.(() => forceUpdate({}));
    return () => subscription?.unsubscribe?.();
  }, [filter]);

  const key = JSON.stringify(filter.getMongoFilter() ?? {});
  const { value, loading, error } = useQuery(() => count(filter), [key]);

  if (error) return nothing;
  const total = value ?? null;
  return html`
    <div class="px-4 pb-1 text-xs text-base-content/60">
      ${total == null
        ? loading
          ? html`Counting…`
          : nothing
        : html`${total.toLocaleString()} ${total === 1 ? 'record' : 'records'}`}
    </div>
  `;
});

/**
 * A table list view for the ERM data-view that shows a "<n> records" line reflecting the current
 * filter/search (computed server-side via `count`), above the built-in {@link tableView}. The table
 * itself is unchanged; only the count line is added.
 *
 * `renderActions` optionally renders a toolbar (e.g. a bulk-action button) on the right of the count
 * line; it receives the view context, so it can read the live `ctx.filter`.
 */
export const countingTableView = <T extends typeof AdornisEntity>(
  count: RecordCounter,
  renderActions?: (ctx: DataViewContext<T>) => TemplateResult,
): DataViewDefinition<T> => {
  const base = tableView<T>();
  return {
    ...base,
    render: ctx =>
      renderActions
        ? html`
            <div class="flex items-center justify-between gap-2 pr-4">
              ${RecordCount({ filter: ctx.filter, count })}${renderActions(ctx)}
            </div>
            ${base.render(ctx)}
          `
        : html`${RecordCount({ filter: ctx.filter, count })}${base.render(ctx)}`,
  };
};
